import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TenantService } from '../../core/tenant.service';
import { SessionService } from '../../core/session.service';
import { AuthService } from '../../core/auth.service';
import { PermissionsService } from '../../core/permissions.service';
import { QuoteService } from '../../core/quote.service';
import { DataService } from '../../core/data.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  searchText = '';
  showSearchOptions = false;
  showLiveResults = false;
  liveResults: any[] = [];
  liveLoading = false;

  // Active-quote dropdown (V1 parity: ul.dropdown-notification under #currentQuoteButton)
  showCartDropdown = false;

  private searchSubject = new Subject<string>();

  constructor(
    public tenant: TenantService,
    public session: SessionService,
    public perms: PermissionsService,
    private auth: AuthService,
    private quoteSvc: QuoteService,
    private dataSvc: DataService,
    private router: Router,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Debounced live search (400ms like V1)
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      if (term.length > 1) {
        this.liveLoading = true;
        this.dataSvc.dgGet([{
          action: 'SearchablesGet', returnas: 'BASE',
          searchText: term, short: '1'
        }]).subscribe({
          next: (res) => {
            let data = res;
            if (Array.isArray(data) && data[0]?.Data) data = data[0].Data;
            this.liveResults = Array.isArray(data) ? data : [];
            this.liveLoading = false;
          },
          error: () => { this.liveLoading = false; }
        });
      }
    });
  }

  // Close dropdowns when clicking outside
  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.showSearchOptions = false;
      this.showLiveResults = false;
      this.showCartDropdown = false;
    }
  }

  get userName(): string {
    const user = this.session.userInfo;
    if (!user) return '';
    return (user.name_first || '') + ' ' + (user.name_last || '');
  }

  get viewQuote(): any {
    return this.session.data['mdViewQuote'];
  }

  get hasQuote(): boolean {
    return !!this.viewQuote?.quote_id;
  }

  // V1 used mdViewQuote.item_qty (pre-summed by the proc); fall back to Lines length.
  get quoteItemCount(): number {
    return this.viewQuote?.item_qty ?? this.viewQuote?.Lines?.length ?? 0;
  }

  get quoteTotal(): number {
    const lines = this.viewQuote?.Lines || [];
    return lines.reduce((sum: number, l: any) => sum + (l.price > 0 ? l.price * l.qty : 0), 0);
  }

  // Search options tree from user profile
  get searchList(): any[] {
    return this.session.userInfo?.search_list || [];
  }

  get globalSearchKeys(): string[] {
    return this.session.userInfo?.search_options || [];
  }

  set globalSearchKeys(keys: string[]) {
    if (this.session.userInfo) {
      this.session.userInfo.search_options = keys;
    }
  }

  logout(): void {
    this.auth.logout();
  }

  // Full search - navigate to results page
  onSearch(): void {
    if (this.searchText.trim()) {
      this.showLiveResults = false;
      this.showSearchOptions = false;
      this.router.navigate(['/search', this.searchText.trim()]);
    }
  }

  // Live search as user types
  onSearchInput(): void {
    if (this.searchText.length > 1) {
      this.showLiveResults = true;
      this.searchSubject.next(this.searchText);
    } else {
      this.showLiveResults = false;
      this.liveResults = [];
    }
  }

  onSearchFocus(): void {
    if (this.searchText.length > 1 && this.liveResults.length > 0) {
      this.showLiveResults = true;
    }
  }

  // Click on a live result
  goToResult(item: any): void {
    this.searchText = '';
    this.showLiveResults = false;
    this.liveResults = [];
    switch (item.type) {
      case '1': this.router.navigate(['/product', item.optDisplay?.replace('/', '%2F')]); break;
      case '2': this.router.navigate(['/quotes', item.searchID]); break;
      case '3': this.router.navigate(['/orders', item.searchID]); break;
      case '4': this.router.navigate(['/rmas', item.searchID]); break;
    }
  }

  getResultIcon(type: string): string {
    switch (type) {
      case '1': return 'pi pi-cog';
      case '2': return 'pi pi-file';
      case '3': return 'pi pi-truck';
      case '4': return 'pi pi-replay';
      default: return 'pi pi-cog';
    }
  }

  getResultPrefix(type: string): string {
    switch (type) {
      case '1': return '';
      case '2': return 'QT #:';
      case '3': return 'SO #:';
      case '4': return 'RMA #:';
      default: return '';
    }
  }

  getResultDisplay(item: any): string {
    if (item.optDisplay) return item.optDisplay + ' - ' + (item.description || '');
    if (item.fieldtype) return item.searchID + ' - ' + (item.searchabletext || '');
    return item.searchID || '';
  }

  // Search options tree checkbox logic
  toggleSearchOptions(event: Event): void {
    event.stopPropagation();
    this.showSearchOptions = !this.showSearchOptions;
  }

  /** Get checkbox state for a tree node: 0=none, 1=partial, 2=all */
  getCheckState(node: any): number {
    const keys = this.globalSearchKeys;
    if (Array.isArray(node.children)) {
      const childKeys = this.flattenKeys(node.children);
      const selected = childKeys.filter(k => keys.includes(k)).length;
      if (selected === 0) return 0;
      if (selected === childKeys.length) return 2;
      return 1;
    }
    return keys.includes(node.key) ? 2 : 0;
  }

  getCheckIcon(node: any): string {
    const state = this.getCheckState(node);
    if (state === 2) return 'pi pi-check-square';
    if (state === 1) return 'pi pi-minus';
    return 'pi pi-stop';
  }

  toggleNode(node: any, event: Event): void {
    event.stopPropagation();
    let keys = [...this.globalSearchKeys];

    if (Array.isArray(node.children)) {
      const childKeys = this.flattenKeys(node.children);
      const selected = childKeys.filter(k => keys.includes(k));
      if (selected.length < childKeys.length) {
        // Select all children
        keys = [...new Set([...keys, ...childKeys])];
      } else {
        // Deselect all children
        keys = keys.filter(k => !childKeys.includes(k));
      }
    } else {
      if (keys.includes(node.key)) {
        keys = keys.filter(k => k !== node.key);
      } else {
        keys.push(node.key);
      }
    }

    this.globalSearchKeys = keys;
    this.saveSearchOptions(keys);
  }

  private flattenKeys(nodes: any[]): string[] {
    const keys: string[] = [];
    for (const n of nodes) {
      if (Array.isArray(n.children)) {
        keys.push(...this.flattenKeys(n.children));
      } else if (n.key) {
        keys.push(n.key);
      }
    }
    return keys;
  }

  private saveSearchOptions(keys: string[]): void {
    const ecomKey = this.session.userInfo?.ecom_update_key || '';
    if (!ecomKey) return;
    this.dataSvc.dsSave([{
      table: 'cur',
      type: 'upsert',
      source: 'custportal',
      rows: [{ keys: { EcomUpdateKey: ecomKey }, columns: { SearchOptions: keys.join(','), ItemSearchTerm: '' } }]
    }]).subscribe();
  }

  // ── New Quote (V1: #aNewQuote — used to be a dropdown, now opens directly) ──
  newQuote(): void {
    this.session.data['mdViewQuote'] = null;
    this.router.navigate(['/quotes', '0']);
  }

  // ── Cart dropdown (V1: #currentQuoteButton) ────────────────────────────────
  // The button itself is hidden when !hasQuote, so we only need the toggle path.
  toggleCartDropdown(event: Event): void {
    event.stopPropagation();
    this.showCartDropdown = !this.showCartDropdown;
  }

  /** "Go to Quote" button inside the cart dropdown. V1's ViewQuote(viewQuote). */
  goToQuote(): void {
    const id = this.viewQuote?.quote_id;
    this.showCartDropdown = false;
    if (id) this.router.navigate(['/quotes', id]);
  }

  /** Click on a line inside the cart dropdown — opens that product's page. */
  goToCartLine(line: any): void {
    this.showCartDropdown = false;
    if (line?.part_nr) {
      this.router.navigate(['/product', line.part_nr]);
    }
  }

  /** True for lines flagged as price-requested (V1: rslt.price == '-1.00'). */
  isPriceDenied(line: any): boolean {
    return String(line?.price) === '-1.00';
  }
}
